import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import apiClient from '@/utils/api';
import { MapPin, Calendar, CheckCircle2, Circle, Clock, Palette, ArrowLeft } from 'lucide-react';

const InteriorPortfolio = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [slug]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/interior-projects/my-project`);
      const projects = res.data?.projects || [];
      const found = projects.find(p => p.project_id === slug);
      if (found) setProject(found);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-6">This interior project portfolio page is not available.</p>
          <Link to="/interior-design" className="text-[#15803D] hover:underline">Back to Interior Design</Link>
        </div>
      </div>
    );
  }

  const pageTitle = `Interior Project: ${project.property_address} | ZenStudios`;
  const pageDescription = `View the interior design project at ${project.property_address}. Progress: ${project.progress_percent}%. Designed by ${project.assigned_designer || 'ZenStudios'}.`;

  return (
    <div className="min-h-screen bg-[#F5F5F0] py-32 px-6" data-testid="interior-portfolio-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://zenstudios.in/interiors/project/${slug}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <Link to="/interior-design" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#15803D] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Interior Design
        </Link>

        <div className="bg-white p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{project.property_address}</h1>
              <p className="text-gray-600">Customer: {project.customer_name}</p>
            </div>
            <span className="px-4 py-2 bg-green-100 text-green-700 text-sm rounded capitalize">
              {(project.project_status || '').replace(/_/g, ' ')}
            </span>
          </div>

          <div className="grid md:grid-cols-4 gap-4 text-sm mb-6">
            <div><p className="text-gray-500">Designer</p><p className="font-medium">{project.assigned_designer || '-'}</p></div>
            <div><p className="text-gray-500">Site Engineer</p><p className="font-medium">{project.assigned_site_engineer || '-'}</p></div>
            <div><p className="text-gray-500">Start Date</p><p className="font-medium">{project.start_date || '-'}</p></div>
            <div><p className="text-gray-500">Completion</p><p className="font-medium">{project.estimated_completion_date || '-'}</p></div>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Project Progress</span>
              <span className="font-bold text-[#15803D]">{project.progress_percent}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3">
              <div className="bg-[#15803D] h-3 rounded-full transition-all" style={{ width: `${project.progress_percent}%` }} />
            </div>
          </div>
        </div>

        <div className="bg-white p-8">
          <h2 className="text-2xl font-bold mb-6">Milestone Timeline</h2>
          <div className="space-y-4">
            {(project.milestones || []).map((ms) => (
              <div key={ms.milestone_id} className="flex gap-4" data-testid={`portfolio-milestone-${ms.milestone_id}`}>
                <div className="mt-0.5 flex-shrink-0">
                  {ms.milestone_status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-[#15803D]" /> :
                   ms.milestone_status === 'in_progress' ? <Clock className="w-5 h-5 text-amber-500" /> :
                   <Circle className="w-5 h-5 text-gray-300" />}
                </div>
                <div className="flex-1 pb-4 border-b border-gray-100 last:border-0">
                  <h3 className={`font-medium ${ms.milestone_status === 'completed' ? 'text-[#15803D]' : ms.milestone_status === 'in_progress' ? 'text-amber-700' : 'text-gray-400'}`}>
                    {ms.milestone_name}
                  </h3>
                  {ms.photos && ms.photos.length > 0 && (
                    <div className="flex gap-3 mt-3 overflow-x-auto">
                      {ms.photos.map((photo, pi) => (
                        <div key={pi} className="flex-shrink-0">
                          <img
                            src={photo.image_url}
                            alt={photo.caption || ms.milestone_name}
                            className="w-32 h-24 rounded-lg object-cover border"
                            loading="lazy"
                          />
                          {photo.caption && <p className="text-xs text-gray-500 mt-1">{photo.caption}</p>}
                          {photo.upload_timestamp && (
                            <p className="text-xs text-gray-400">{new Date(photo.upload_timestamp).toLocaleDateString('en-IN')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteriorPortfolio;
